import { useState } from 'react';
import { generatePath, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { APPOINTMENT_STATUS_CODE } from '@/constants/appointmentOptions';
import { useAppointmentsQuery } from '@/hooks/useAppointmentsQuery';
import { useCancelAppointmentMutation } from '@/hooks/useCancelAppointmentMutation';
import { useChangeAppointmentStatusMutation } from '@/hooks/useChangeAppointmentStatusMutation';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

export type VisitQuickAction = 'writeNote' | 'noShow' | 'confirm' | 'cancel';

interface VisitQuickActionsProps {
  appointmentId: string;
  patientId: string;
  actions: VisitQuickAction[];
}

export function VisitQuickActions({ appointmentId, patientId, actions }: VisitQuickActionsProps) {
  const { t } = useTranslation('dashboard');
  const appointmentsQuery = useAppointmentsQuery();
  const statusMutation = useChangeAppointmentStatusMutation();
  const cancelMutation = useCancelAppointmentMutation();
  const [isCancelConfirmOpen, setIsCancelConfirmOpen] = useState(false);

  const appointment =
    appointmentsQuery.data === undefined
      ? undefined
      : appointmentsQuery.data.find(function matchVisit(candidate) {
          return candidate.id === appointmentId;
        });
  const isBusy = statusMutation.isPending || cancelMutation.isPending;

  async function changeStatus(status: string, successMessageKey: string) {
    if (appointment === undefined) {
      return;
    }
    try {
      await statusMutation.changeStatus({ appointment, status });
      showSuccessNotification(t(successMessageKey));
    } catch {
      showErrorNotification(t('visitActions.error'));
    }
  }

  async function cancelVisit() {
    try {
      await cancelMutation.mutateAsync(appointmentId);
      setIsCancelConfirmOpen(false);
      showSuccessNotification(t('visitActions.cancelled'));
    } catch {
      showErrorNotification(t('visitActions.error'));
    }
  }

  if (actions.length === 0) {
    return null;
  }

  const writeNotePath =
    generatePath(APPLICATION_ROUTES.workspacePatientSessionNote, { patientId, noteId: 'new' }) +
    '?appointmentId=' +
    appointmentId;

  return (
    <div className="flex flex-wrap gap-1 pb-2">
      {actions.includes('writeNote') ? (
        <Link
          to={writeNotePath}
          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-lg bg-zooprimary text-zoowhite hover:bg-zooprimaryhover"
        >
          {t('visitActions.writeNote')}
        </Link>
      ) : null}
      {actions.includes('confirm') ? (
        <Button
          type="button"
          variant="outlinePrimary"
          size="chip"
          disabled={isBusy || appointment === undefined}
          onClick={function confirmVisit() {
            void changeStatus(APPOINTMENT_STATUS_CODE.confirmed, 'visitActions.confirmed');
          }}
        >
          {t('visitActions.confirm')}
        </Button>
      ) : null}
      {actions.includes('noShow') ? (
        <Button
          type="button"
          variant="outline"
          size="chip"
          disabled={isBusy || appointment === undefined}
          onClick={function markNoShow() {
            void changeStatus(APPOINTMENT_STATUS_CODE.noShow, 'visitActions.markedNoShow');
          }}
        >
          {t('visitActions.noShow')}
        </Button>
      ) : null}
      {actions.includes('cancel') ? (
        <Button
          type="button"
          variant="ghost"
          size="chip"
          disabled={isBusy}
          onClick={function askToCancel() {
            setIsCancelConfirmOpen(true);
          }}
        >
          {t('visitActions.cancel')}
        </Button>
      ) : null}
      {isCancelConfirmOpen ? (
        <ConfirmDialog
          title={t('visitActions.cancelConfirmTitle')}
          description={t('visitActions.cancelConfirmDescription')}
          confirmLabel={t('visitActions.cancelConfirm')}
          tone="danger"
          isConfirming={cancelMutation.isPending}
          onConfirm={function confirmCancel() {
            void cancelVisit();
          }}
          onCancel={function closeCancelConfirm() {
            setIsCancelConfirmOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}
